import { authenticatedFetch, resolveApiUrl } from "@/lib/api";
import type { SlackImportJob, SlackPreview } from "./types";

export class SlackImportApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = "SlackImportApiError";
  }
}

export class SlackImportApi {
  constructor(private readonly fetcher: typeof fetch = authenticatedFetch) {}

  private async request<T>(method: "GET" | "POST", path: string, body?: unknown): Promise<T> {
    const headers: Record<string, string> = { Accept: "application/json" };
    if (body !== undefined) headers["Content-Type"] = "application/json";
    const res = await this.fetcher(`${resolveApiUrl("")}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    const text = await res.text();
    const parsed = text ? JSON.parse(text) as { error?: { code?: string; message?: string } } : null;
    if (!res.ok) {
      throw new SlackImportApiError(
        res.status,
        parsed?.error?.code ?? "UNKNOWN_ERROR",
        parsed?.error?.message ?? `HTTP ${res.status}`,
      );
    }
    return parsed as T;
  }

  async parse(file: File): Promise<{ job: SlackImportJob; idempotent: boolean }> {
    const response = await this.fetcher(resolveApiUrl('/v1/slack-imports/parse'), { method: 'POST', headers: { 'content-type': 'application/octet-stream', 'x-archive-name': encodeURIComponent(file.name) }, body: file });
    const body = await response.json();
    if (!response.ok) throw new SlackImportApiError(response.status, body.error?.code ?? 'IMPORT_ERROR', body.error?.message ?? 'Import failed');
    return body;
  }

  preview(job_id: string) {
    return this.request<{ job_id: string; preview: SlackPreview }>("POST", "/v1/slack-imports/preview", { job_id });
  }

  start(job_id: string, selected_channel_ids: string[], mapping: Record<string, string>, acknowledge_private: boolean, channel_mapping: Record<string, string> = {}) {
    return this.request<{ job: SlackImportJob }>("POST", "/v1/slack-imports/start", {
      job_id,
      selected_channel_ids,
      mapping,
      acknowledge_private,
      channel_mapping,
    });
  }

  job(job_id: string) {
    return this.request<SlackImportJob>("GET", `/v1/slack-imports/jobs/${job_id}`);
  }

  cancel(job_id: string) {
    return this.request<SlackImportJob>("POST", `/v1/slack-imports/jobs/${job_id}/cancel`, {});
  }

  retry(job_id: string) {
    return this.request<SlackImportJob>("POST", `/v1/slack-imports/jobs/${job_id}/retry`, {});
  }
}
